import {
  createTool,
  deleteTool,
  fetchAllTools,
  fetchToolById,
  updateTool,
} from '@/services/tool.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { PaginatedResponse, PaginationParams, QueryOptions } from '@/types/common.type';
import type { Tool, ToolCreateRequest, ToolUpdateRequest } from '@/types/tool.type';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

export const useToolsQuery = (
  params: PaginationParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<Tool>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.tools.list(params.page || 1, params.page_size, params.query || ''),
    queryFn: () => fetchAllTools(params),
    ...options,
  });
};

export const useToolDetailQuery = (
  toolId: string,
  options?: Omit<UseQueryOptions<Tool, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.tools.detail(toolId),
    queryFn: () => fetchToolById(toolId),
    ...options,
  });
};

export const useCreateToolMutation = (options?: QueryOptions<Tool>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.tools.mutations.create,
    mutationFn: (payload: ToolCreateRequest) => createTool(payload),
    onSuccess: data => {
      notifyMutationSuccessWithOptions(options, 'Tool created', 'Tool was created successfully.');
      queryClient.invalidateQueries({ queryKey: queryKeys.tools.all });
      options?.onSuccess?.(data);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Tool creation failed', error);
      options?.onError?.(error);
    },
  });
};

export const useUpdateToolMutation = (options?: QueryOptions<Tool>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.tools.mutations.update,
    mutationFn: ({ toolId, payload }: { toolId: string; payload: ToolUpdateRequest }) =>
      updateTool(toolId, payload),
    onSuccess: data => {
      notifyMutationSuccessWithOptions(options, 'Tool updated', 'Tool was updated successfully.');
      queryClient.invalidateQueries({ queryKey: queryKeys.tools.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tools.detail(data.id) });
      options?.onSuccess?.(data);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Tool update failed', error);
      options?.onError?.(error);
    },
  });
};

export const useDeleteToolMutation = (options?: QueryOptions<null>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.tools.mutations.delete,
    mutationFn: (toolId: string) => deleteTool(toolId),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(options, 'Tool deleted', 'Tool was deleted successfully.');
      queryClient.invalidateQueries({ queryKey: queryKeys.tools.all });
      options?.onSuccess?.(null);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Tool deletion failed', error);
      options?.onError?.(error);
    },
  });
};
